import React, { useEffect, useState } from 'react'
import axios from 'axios'
import PageHeader from '../../shared/page-header/PageHeader'
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts'
import { getApiBaseUrl } from '../../utils/apiConfig'
import { getModulePermissions } from '../../utils/permissionHelper'
import { exportToExcel, exportToPDF } from '../../utils/downloadExportedFiles'
import { logAuditEvent } from '../../utils/auditLogger'
import { getStorageValues } from '../../shared/CommonFunctions'
import HandoverRegisterGrid from './HandoverRegisterGrid'
import { useLocation, useNavigate } from 'react-router-dom'

function HandoverRegisterMain(props) {
  const [eligibleRows, setEligibleRows] = useState([])
  const [completedRows, setCompletedRows] = useState([])
  const [showCompleted, setShowCompleted] = useState(false)
  const [alert, setAlert] = useState(null)
  const [userPermissions, setUserPermissions] = useState([])
  const [highlightSalesRegisterId, setHighlightSalesRegisterId] = useState(null)
  const moduleName = props.Module || 'Handover Register'
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const modulePermissions = getModulePermissions(props.Module)
    setUserPermissions(modulePermissions || [])
    fetchEligible()
  }, [props.Module])

  useEffect(() => {
    logAuditEvent(moduleName, 'Accessed Module', `Accessed ${moduleName}`)
  }, [moduleName])

  const fetchEligible = async () => {
    try {
      const params = { UserId: getStorageValues('userId') }
      const response = await axios.get(getApiBaseUrl() + '/handoverregister/eligible', { params })
      if (response.status === 200) {
        setEligibleRows(response.data.data || [])
      }
    } catch (error) {
      console.log(error)
      CreateAlert('Unable to load eligible handover records', 'Error', setAlert)
    }
  }

  const fetchCompleted = async () => {
    try {
      const params = { UserId: getStorageValues('userId') }
      const response = await axios.get(getApiBaseUrl() + '/handoverregister/getall', { params })
      if (response.status === 200) {
        const rows = response.data.data || []
        // "getall" returns all handover rows; this toggle is specifically for completed ones.
        // Defensive: if backend doesn't return Status column (wrong resultset), don't hide everything.
        const list = Array.isArray(rows) ? rows : []
        const hasStatus = list.some((r) => r && typeof r === 'object' && Object.prototype.hasOwnProperty.call(r, 'Status'))
        if (!hasStatus) {
          setCompletedRows(list)
          return
        }
        const completedOnly = list.filter((r) => {
          const st = String(r?.Status || '').trim().toLowerCase()
          return st === 'complete' || st === 'completed'
        })
        setCompletedRows(completedOnly)
      }
    } catch (error) {
      console.log(error)
      CreateAlert('Unable to load completed handover records', 'Error', setAlert)
    }
  }

  useEffect(() => {
    const defaultSalesId = location?.state?.DefaultSalesRegisterId
    if (defaultSalesId) {
      setHighlightSalesRegisterId(defaultSalesId)
      navigate(location.pathname, { replace: true, state: {} })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.DefaultSalesRegisterId])

  const handleToggleShowCompleted = async (checked) => {
    setShowCompleted(checked)
    if (checked) {
      await fetchCompleted()
      return
    }
    setCompletedRows([])
  }

  const handleExportExcel = () => {
    const columns = [
      { name: 'Customer Sales#', selector: 'SalesNumber' },
      { name: 'Customer Name', selector: 'CustomerName' },
      { name: 'Payment Mode', selector: 'PaymentMode' },
      { name: 'Customer Mobile', selector: 'MobileNumber' },
      { name: 'Status', selector: 'Status' }
    ]
    const data = showCompleted ? [...(eligibleRows || []), ...(completedRows || [])] : (eligibleRows || [])
    exportToExcel('HandoverRegister', columns, data)
  }

  const handleExportPDF = () => {
    const columns = [
      { name: 'Customer Sales#', selector: 'SalesNumber' },
      { name: 'Customer Name', selector: 'CustomerName' },
      { name: 'Payment Mode', selector: 'PaymentMode' },
      { name: 'Customer Mobile', selector: 'MobileNumber' },
      { name: 'Status', selector: 'Status' }
    ]
    const data = showCompleted ? [...(eligibleRows || []), ...(completedRows || [])] : (eligibleRows || [])
    exportToPDF('HandoverRegister', columns, data)
  }

  return (
    <>
      <SKAlerts alert={alert} />
      <PageHeader
        displayModule={props.Module}
        onRefresh={fetchEligible}
        showPDF={true}
        showExcel={true}
        showPrint={false}
        showRefresh={true}
        onExcelExport={handleExportExcel}
        onPdfExport={handleExportPDF}
        showAddButton={false}
      />
      <HandoverRegisterGrid
        EligibleData={eligibleRows}
        CompletedData={showCompleted ? completedRows : []}
        RefreshEligible={fetchEligible}
        UserPermissions={userPermissions}
        moduleName={moduleName}
        HighlightSalesRegisterId={highlightSalesRegisterId}
        ShowCompleted={showCompleted}
        OnToggleShowCompleted={handleToggleShowCompleted}
      />
    </>
  )
}

export default HandoverRegisterMain
